import * as mammoth from "mammoth";
import { pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export const MAX_SMART_SCREEN_FILE_BYTES = 1048576;
export const MIN_READABLE_CV_TEXT_LENGTH = 80;

export const isPdfFile = (file: File): boolean => {
  const name = file.name.toLowerCase();
  const type = (file.type || "").toLowerCase();
  return type.includes("pdf") || name.endsWith(".pdf");
};

export const isWordDocument = (file: File): boolean => {
  const name = file.name.toLowerCase();
  const type = (file.type || "").toLowerCase();
  return (
    name.endsWith(".doc") ||
    name.endsWith(".docx") ||
    type.includes("msword") ||
    type.includes("wordprocessingml")
  );
};

export const toPdfFileName = (fileName: string): string => {
  const base = fileName.replace(/\.[^.]+$/, "").trim() || "cv";
  return `${base}.pdf`;
};

const isDocxFile = (file: File): boolean => {
  const name = file.name.toLowerCase();
  const type = (file.type || "").toLowerCase();
  return type.includes("wordprocessingml") || name.endsWith(".docx");
};

/**
 * Extract selectable text from PDF/DOCX. Throws when the file cannot be read.
 * Empty string means no usable text (scanned/image CV).
 */
type PdfTextItem = {
  str?: string;
  width?: number;
  transform: number[];
};

const ORG_FOOTER =
  /\b(LIMITED|LTD\.?|PLC|INC\.?|LLC|LLP)\b/i;

/** Rebuild lines from PDF text positions instead of joining the whole page into one string. */
const assemblePdfPageLines = (items: PdfTextItem[]): string[] => {
  const usable = items.filter((item) => (item.str ?? "").length > 0);
  const sorted = [...usable].sort((a, b) => {
    const dy = b.transform[5] - a.transform[5];
    if (Math.abs(dy) > 2) return dy;
    return a.transform[4] - b.transform[4];
  });

  const lines: { y: number; parts: { x: number; end: number; str: string }[] }[] =
    [];

  for (const item of sorted) {
    const y = item.transform[5];
    const x = item.transform[4];
    const str = item.str ?? "";
    const end = x + (item.width ?? str.length * 4);
    const current = lines[lines.length - 1];

    if (!current || Math.abs(current.y - y) > 3) {
      lines.push({ y, parts: [{ x, end, str }] });
      continue;
    }

    current.parts.push({ x, end, str });
  }

  return lines
    .map((line) => {
      const parts = [...line.parts].sort((a, b) => a.x - b.x);
      let text = "";
      let previousEnd = -1;

      for (const part of parts) {
        if (!part.str) continue;
        const needsSpace =
          text.length > 0 &&
          previousEnd >= 0 &&
          part.x - previousEnd > 1.5 &&
          !text.endsWith(" ") &&
          !part.str.startsWith(" ");
        text += (needsSpace ? " " : "") + part.str;
        previousEnd = Math.max(previousEnd, part.end);
      }

      return text.replace(/[ \t]+/g, " ").trim();
    })
    .filter(Boolean);
};

/**
 * Drop a short line that is repeated on every page. That is how a printer
 * footer such as "SNAPNET LIMITED" gets glued onto the next employer.
 */
const stripRepeatedPageFurniture = (pages: string[][]): string => {
  const frequency = new Map<string, number>();

  for (const lines of pages) {
    const seen = new Set<string>();
    for (const line of lines) {
      const key = line.trim();
      if (key.length < 3 || key.length > 60 || seen.has(key)) continue;
      seen.add(key);
      frequency.set(key, (frequency.get(key) ?? 0) + 1);
    }
  }

  const furniture = new Set<string>();
  frequency.forEach((count, line) => {
    if (count < 2 || pages.length < 2) return;
    if (/^\d+$/.test(line)) {
      furniture.add(line);
      return;
    }
    const words = line.split(/\s+/);
    if (words.length <= 5 && ORG_FOOTER.test(line)) {
      furniture.add(line);
    }
  });

  return pages
    .map((lines) => lines.filter((line) => !furniture.has(line.trim())).join("\n"))
    .filter(Boolean)
    .join("\n\n")
    .replace(/[ \t]+\n/g, "\n")
    .trim();
};

export const extractRawTextFromDocument = async (file: File): Promise<string> => {
  if (isPdfFile(file)) {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
    const pages: string[][] = [];

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      pages.push(assemblePdfPageLines(textContent.items as PdfTextItem[]));
    }

    return stripRepeatedPageFurniture(pages);
  }

  if (isDocxFile(file)) {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return (result.value || "").replace(/\s+/g, " ").trim();
  }

  return "";
};

export const isCvTextReadable = (text: string | null | undefined): boolean =>
  (text || "").trim().length >= MIN_READABLE_CV_TEXT_LENGTH;

export const extractTextFromAttachment = async (file: File): Promise<string> => {
  try {
    const text = await extractRawTextFromDocument(file);
    return text || `Job Description File: ${file.name}`;
  } catch (error) {
    console.error("Error extracting text:", error);
    return `Job Description File: ${file.name}`;
  }
};
